import React, { useEffect, useState } from "react";
import api, { STAGE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingUp, FolderKanban, CheckCircle2, Clock, AlertTriangle, Users, FileText, Bell, Mail, IndianRupee, DollarSign, ArrowUp, ArrowDown, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import SurveydraftsActivityPanel from "@/components/dashboard/SurveydraftsActivityPanel";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import { KpiLine } from "./StatTiles";

export function WeeklyDigestTile() {
  const [latest, setLatest] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/digest/latest");
      setLatest(data?.empty ? null : data);
    } catch {/* ignore */}
  };

  useEffect(() => { load(); }, []);

  const runNow = async (sendEmail) => {
    setRunning(true);
    try {
      const { data } = await api.post("/digest/run-now", null, { params: { send_email: sendEmail } });
      setLatest(data);
      if (sendEmail) {
        const n = data.email_sent_count || 0;
        if (n > 0) toast.success(`Digest emailed to ${n} admin${n === 1 ? "" : "s"}`);
        else toast.error("Build OK, but no SMTP sender available to send email");
      } else {
        toast.success("Digest rebuilt");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not run digest");
    }
    setRunning(false);
  };

  const fmtAgo = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const mins = Math.round((Date.now() - d.getTime()) / 60000);
      if (mins < 60) return `${mins}m ago`;
      if (mins < 24 * 60) return `${Math.round(mins / 60)}h ago`;
      return `${Math.round(mins / 1440)}d ago`;
    } catch { return iso?.slice(0, 16); }
  };

  return (
    <div className="brut-card p-5" data-testid="weekly-digest-widget">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="label-mono flex items-center gap-1.5">
            <Bell className="w-3 h-3" /> WEEKLY DIGEST
          </div>
          <div className="font-heading text-lg font-bold">Monday morning · admin email</div>
        </div>
        <span className="label-mono text-zinc-500" data-testid="digest-last-run">
          {latest ? `LAST: ${fmtAgo(latest.computed_at)}` : "NEVER"}
        </span>
      </div>
      {latest ? (
        <div className="grid grid-cols-2 gap-2 text-xs font-mono mb-3">
          <KpiLine label="Runway" value={latest.runway_months !== null && latest.runway_months !== undefined ? `${latest.runway_months}mo` : "—"} />
          <KpiLine label="Stuck jobs" value={String(latest.stuck_total ?? 0)} bad={latest.stuck_total > 0} />
          <KpiLine label="Util avg" value={`${latest.team_utilization_avg ?? 0}%`} />
          <KpiLine label="Overdue inv" value={String(latest.overdue_total ?? 0)} bad={latest.overdue_total > 0} />
        </div>
      ) : (
        <div className="text-xs font-mono text-zinc-500 mb-3">Digest hasn&apos;t been generated yet. Run one to preview.</div>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => runNow(false)}
          disabled={running}
          className="brut-btn-secondary px-3 py-1.5 text-xs disabled:opacity-50"
          data-testid="digest-rebuild-btn"
        >
          {running ? "REBUILDING…" : "REBUILD ONLY"}
        </button>
        <button
          onClick={() => runNow(true)}
          disabled={running}
          className="brut-btn px-3 py-1.5 text-xs disabled:opacity-50"
          data-testid="digest-send-btn"
        >
          REBUILD + EMAIL ADMINS
        </button>
      </div>
    </div>
  );
}



// ----------------------------- Outstanding Balances -----------------------------
